/**
 * Revision History
 * 
 * 12/01/2021 ptkim       Add appendLine
 */
import _ from 'lodash';
import { History } from 'history';
import React from 'react';

import constants from '../constants';
import { getCurrencies, getFmsGlobal } from '../globals';

import { BillInvoiceLineItem } from '../ifmsTypes';
import { round as mathRound } from 'mathjs';
import { InvoDetail } from "../fms.core/InvoDetail";
import { EawbBrokerType } from '../fms.core/pims/Eawb';
import { ApDetail } from "../fms.core/ApDetail";
import { IfmsConfigMenuItem } from '../config/types';
import { SecurityGrant } from '../framework.core/SecurityGrant';
import { FmsSecTask } from '../fms.core/constants';
import { JobNames, getBLTypeByJobName, getHouseJob, getMasterJob, getJobNameByTbName } from '../framework.core/JobNames';
import { FormType, SecType } from '../fms.enum';
import { IApplicationContext } from '../ApplicationContext';

import { OiMblListItem } from '../fms.data/models/OiMblListItem';
import { OiHblListItem } from '../fms.data/models/OiHblListItem';
import { AiMblListItem } from '../fms.data/models/AiMblListItem';
import { AiHblListItem } from '../fms.data/models/AiHblListItem';
import { OeMblListItem } from '../fms.data/models/OeMblListItem';
import { OeHblListItem } from '../fms.data/models/OeHblListItem';
import { AeMblListItem } from '../fms.data/models/AeMblListItem';
import { AeHblListItem } from '../fms.data/models/AeHblListItem';
import { OtherOperationListItem } from '../fms.data/models/OtherOperationListItem';
import { WhReciptsListItem } from '../fms.data/models/WhReciptsListItem';
import { WhRcvListItem } from '../fms.data/models/WhRcvListItem';
import { WhShipListItem } from '../fms.data/models/WhShipListItem';
import { CodeCompanyListItem } from '../fms.data/models/CodeCompanyListItem';
import { PimsBookingListItem } from '../fms.data/models/PimsBookingListItem';
import { PimsVanningListItem } from '../fms.data/models/PimsVanningListItem';
import { OeBookingListItem } from '../fms.data/models/OeBookingListItem';
import { BillPaymentListItem } from '../fms.data/models/BillPaymentListItem';
import { BatchRecogListItem } from '../fms.data/models/BatchRecogListItem';
import { InvoiceListItem } from '../fms.data/models/InvoiceListItem';
import { BillListItem } from '../fms.data/models/BillListItem';
import { CrDbListItem } from '../fms.data/models/CrDbListItem';
import { JournalLedgerListItem } from '../fms.data/models/JournalLedgerListItem';
import { MyEventListItem } from '../fms.data/models/MyEventListItem';
import { IsfListItem } from '../fms.data/models/IsfListItem';
import { JobMenuState } from '../features/jobMenuSlice';
import { OpAcct } from '../fms.core/OpAcct';
import { SalesCostCrDb } from '../fms.data/models/SalesCostCrDb';
import { ExtendedSystemSettings } from '../hooks/useSystemSettings';
import { CustomerShipmentListItem } from '../fms.data/models/CustomerShipmentListItem';



export const OperationsMap = _.map(constants.operations, (value, key) => ({
  key,
  value,
}));

/**
 * @deprecated - Utility function to get layout sizes based on browser size.
 * It was used in old 'responsive' design
 *
 * @export
 * @param {number} viewportWidth - browser width
 */
export const getLayoutSizes = (viewportWidth: number) => {
  if (viewportWidth < 1360) {
    return constants.layout.break4;
  }
  else if (viewportWidth < 1440) {
    return constants.layout.break3;
  }
  else if (viewportWidth < 1680) {
    return constants.layout.break2;
  }
  else if (viewportWidth < 1920) {
    return constants.layout.break1;
  } else {
    return constants.layout.large;
  }
}


/**
 * Utility function to get operation key from name.
 * ocean Import -> oceanImport return.
 *
 * @export
 * @param {string} operationName
 * @return {*}  {(string|null)}
 */
export function getOperationKey(operationName: string): string | null {
  const match = OperationsMap.find(x => x.value === operationName);
  return match ? match.key : null;
}

/**
 * to get Opertaion Name from JobName 
 * OIMB or OIHB > Ocean Import return.
 * @param parent 
 * @returns 
 */
export function getOperationNameFromJobName(jobName: JobNames): string {
  if (jobName === JobNames.OIHbl || jobName === JobNames.OIMbl) {
    return constants.operations.oceanImport;
  } else if (jobName === JobNames.OEHbl || jobName === JobNames.OEMbl) {
    return constants.operations.oceanExport;
  } else if (jobName === JobNames.AIHbl || jobName === JobNames.AIMbl) {
    return constants.operations.airImport;
  } else if (jobName === JobNames.AEHbl || jobName === JobNames.AEMbl) {
    return constants.operations.airExport;
  } else if (jobName === JobNames.OtherOP) {
    return constants.operations.otherShipment;
  } else if (jobName === JobNames.WHRcv || jobName === JobNames.WHShip) {
    return constants.operations.warehousing;
  } else if (jobName === JobNames.RptCustomerShipment) {
    return constants.operations.shipmentStatus;
  }

  throw new Error('Unexpected parent type');
}


/**
 * Utility function to check if str1 contains str2 - ignore case
 *
 * @export
 * @param {(string|null|undefined)} str1
 * @param {(string|null|undefined)} str2
 * @return {*} 
 */
export function stringsContainsIgnoreCase(str1: string | null | undefined, str2: string | null | undefined) {
  if (str1 === null || str1 === undefined || str1 === '' || str2 === null || str2 === undefined || str2 === '') {
    return false;
  }

  return str1.toLowerCase().includes(str2.toLowerCase());
}

/**
 * Utility function to check if str1 equals str2 - ignore case
 *
 * @export
 * @param {(string|null|undefined)} str1
 * @param {(string|null|undefined)} str2
 * @return {*} 
 */
export function stringsEqualIgnoreCase(str1: string | null | undefined, str2: string | null | undefined) {
  if (str1 === null) {
    return str2 === null;
  }
  if (str1 === undefined) {
    return str2 === undefined;
  }
  if (str2 === null) {
    return str1 === null;
  }
  if (str2 === undefined) {
    return str1 === undefined;
  }

  return str1.toLowerCase() === str2.toLowerCase();
}

/**
 * Utility function to check if str1 starts with str2 - ignore case
 *
 * @export
 * @param {(string|null|undefined)} str1
 * @param {(string|null|undefined)} str2
 * @return {*} 
 */
export function stringsStartsWithIgnoreCase(str1: string | null | undefined, str2: string | null | undefined) {
  if (str1 === null || str2 === null || str1 === undefined || str2 === undefined) {
    return false;
  }

  return str1.toLowerCase().startsWith(str2.toLowerCase());
}

/**
 * Convert length, width, height to kgs and lbs
 *
 * @export
 * @return {*}
 * @param calculation
 */
export function dimensionsToKgsLbs({ length, width, height, pcs, volCm, isDomestic, systemSettings }: {
  length: number,
  width: number,
  height: number,
  pcs: number,
  volCm: boolean,
  isDomestic: boolean,
  systemSettings: ExtendedSystemSettings
}) {

  const volWtDomestic = systemSettings?.bl?.volumneWeightDomestic ?? 0;
  const volWtInternational = systemSettings?.bl?.volumeWeightInternational ?? 0;

  let weightCalculationBase = (length ?? 0) * (width ?? 0) * (height ?? 0) * (pcs ?? 0);
  const change = { chgWeight: 0, lChgWeight: 0 }

  if (weightCalculationBase > 0) {
    if (volCm) {
      const kgs = weightCalculationBase / 6000.0;
      change.chgWeight = mathRound(kgs, 0);
      change.lChgWeight = mathRound(kgToLbs(kgs) ?? 0, 1);  // 반올림 전 값 환산
    } else {
      if (isDomestic && volWtDomestic !== 0) weightCalculationBase /= volWtDomestic;
      else if (!isDomestic && volWtInternational !== 0) weightCalculationBase /= volWtInternational;
      else weightCalculationBase /= 166;

      change.lChgWeight = mathRound(weightCalculationBase, 0);
      change.chgWeight = mathRound(lbsToKg(weightCalculationBase) ?? 0, 1);   // 반올림 전 값 환산
    }
  }

  return change;
}

/**
 * Convert length, width, height to cbm and lbs
 *
 * @export
 * @return {*}
 * @param calculation
 * @param includeLbs
 */
export function dimensionsToCbmKgs({ length, width, height, pcs, volCm }: {
  length: number,
  width: number,
  height: number,
  pcs: number,
  volCm: boolean
}, includeLbs = false): { chgWeight: number, lChgWeight?: number } {

  const weightCalculationBase = (length ?? 0) * (width ?? 0) * (height ?? 0) * (pcs ?? 0);

  const change = { chgWeight: 0, lChgWeight: 0 }

  if (weightCalculationBase > 0) {
    if (volCm) {
      change.chgWeight = mathRound(weightCalculationBase / 1000000, 3);
      if (includeLbs) {
        change.lChgWeight = mathRound(weightCalculationBase / 6000 * 2.2046, 2);
      }
    } else {
      change.chgWeight = mathRound((weightCalculationBase / 1728) / 35.3147, 3);
      if (includeLbs) {
        change.lChgWeight = mathRound(weightCalculationBase / 166.0, 2);
      }
    }
  }
  if (!includeLbs) {
    return { chgWeight: change.chgWeight }
  }
  return change;
}

/**
 * Convert kg to lbs
 *
 * @export
 * @param {(number|null|undefined)} n
 * @return {*} 
 */
export function kgToLbs(n: number | null | undefined) {
  if (n === null || n === undefined || isNaN(n)) {
    return null;
  }

  return n * 2.2046;
}

/**
 * Convert lbs to kg
 *
 * @export
 * @param {(number|null|undefined)} n
 * @return {*} 
 */
export function lbsToKg(n: number | null | undefined) {
  if (n === null || n === undefined || isNaN(n)) {
    return null;
  }

  return Math.round(n / 2.2046 * 100) / 100;
}

/**
 * Convert CFT to CBM
 *
 * @export
 * @param {(number|null|undefined)} n
 * @return {*} 
 */
export function cftToCbm(n: number | null | undefined) {
  if (n === null || n === undefined || isNaN(n)) {
    return null;
  }

  return n / 35.3147;
}

/**
 * Check if keyboard event is key press (not function or special key)
 *
 * @export
 * @param {KeyboardEvent} evt
 * @return {*} 
 */
export function isCharacterKeyPress(evt: KeyboardEvent | React.KeyboardEvent) {
  return (!evt.keyCode) ||
    (
      !(evt.keyCode <= 29) &&
      !(evt.keyCode >= 33 && evt.keyCode <= 47) &&
      !(evt.keyCode >= 91 && evt.keyCode <= 95) &&
      !(evt.keyCode >= 112 && evt.keyCode <= 151) &&
      !(evt.keyCode >= 166 && evt.keyCode <= 169) &&
      !(evt.keyCode >= 172 && evt.keyCode <= 183) &&
      !(evt.keyCode >= 224 && evt.keyCode <= 235)
    );
}

/**
 * Utility function to process Bill or Invoice line item delete. It makes sure that if line item with extra currency has been deleted
 * unused currencies and exchange rates are deleted
 *
 */

export function handleBillOrInvoiceLineItemDelete(
  cur1Param: string | null | undefined,
  cur2Param: string | null | undefined,
  exRate1Param: number | null | undefined,
  exRate2Param: number | null | undefined,
  lineItems: Array<BillInvoiceLineItem>
): {
  cur1: string,
  cur2: string,
  exRate1: number | null,
  exRate2: number | null,
} {
  let cur1 = cur1Param ?? '';
  let cur2 = cur2Param ?? '';
  let exRate1 = exRate1Param ?? null;
  let exRate2 = exRate2Param ?? null;

  if (cur2) {
    const used = lineItems.some(x => x.cur === cur2 || x.agtCur === cur2);
    if (!used) {
      cur2 = '';
      exRate2 = null;
    }
  }

  if (cur1) {
    const used = lineItems.some(x => x.cur === cur1 || x.agtCur === cur1);
    if (!used) {
      cur1 = '';
      exRate1 = null;

      // if cur 2 is used - move it to cur1
      if (cur2) {
        cur1 = cur2;
        exRate1 = exRate2;
        cur2 = '';
        exRate2 = null;
      }
    }
  }

  return { cur1, cur2, exRate1, exRate2 };
}
/**
 * Utility function to process Bill or Invoice currency change. It keeps track of all currencies in line items to make sure max 3 currencies are used
 * After analyzing all line items it returns which are the currencies in use, which is currency 1 and 2 and matching exchange rate
 * In case currency 1 is removed (not used any more) it moves currency 2 to currency 1
 *
 * @export
 * @param {(string|null|undefined)} newCur
 * @param {(string|null|undefined)} oldCur
 * @param {Array<string>} availableCurrenciesParam
 * @param {(string|null|undefined)} cur1Param
 * @param {(string|null|undefined)} cur2Param
 * @param {(number|null|undefined)} exRate1Param
 * @param {(number|null|undefined)} exRate2Param
 * @param {(string|null|undefined)} primaryCurrency
 * @param {Array<BillInvoiceLineItem>} lineItems
 * @return {*} 
 */
export function handleBillOrInvoiceCurrencyChange(
  newCur: string | null | undefined,
  oldCur: string | null | undefined,
  availableCurrenciesParam: Array<string>,
  cur1Param: string | null | undefined,
  cur2Param: string | null | undefined,
  exRate1Param: number | null | undefined,
  exRate2Param: number | null | undefined,
  primaryCurrency: string | null | undefined,
  lineItems: Array<BillInvoiceLineItem | InvoDetail | ApDetail>) {
  let cur = newCur;
  let cur1 = cur1Param;
  let cur2 = cur2Param;
  let exRate1 = exRate1Param;
  let exRate2 = exRate2Param;
  let availableCurrencies = availableCurrenciesParam;

  if (!!oldCur && oldCur !== primaryCurrency) {
    const othersUseOldCurrency = lineItems.some(
      x => x.cur === oldCur || (x as BillInvoiceLineItem)?.agtCur === oldCur || (x as InvoDetail)?.agtCur === oldCur
    );
    if (!othersUseOldCurrency) {
      availableCurrencies = availableCurrencies.filter(x => x !== oldCur);
      if (cur1 === oldCur) {
        cur1 = cur2;
        exRate1 = exRate2;
        cur2 = null;
        exRate2 = 0;
      }
      if (cur2 === oldCur) {
        cur2 = null;
        exRate2 = 0;
      }
    }
  }

  if (!!newCur && newCur !== primaryCurrency) {
    if (!availableCurrencies.some(x => x === newCur)) {
      availableCurrencies.push(newCur);
    }

    if (cur1 !== newCur && cur2 !== newCur) {
      if (_.isEmpty(cur1)) {
        cur1 = newCur;
        exRate1 = 0;
      } else {
        cur2 = newCur;
        exRate2 = 0;
      }
    }
  }

  return { currency: cur ?? '', invCur1: cur1 ?? '', invCur2: cur2 ?? '', exRate1: exRate1 || 0, exRate2: exRate2 || 0, availableCurrencies };
}

function applyLineItemCurrencyChange(l: BillInvoiceLineItem, cur: string | null | undefined) {
  l.cur = cur ?? '';
  return l;
  // const r = _.cloneDeep(l);
  // r.cur = cur ?? '';
  // return r;
}

function applyLineItemAgentCurrencyChange(l: BillInvoiceLineItem, agentCur: string | null | undefined) {
  l.agtCur = agentCur ?? '';
  return l;
  // const r = _.cloneDeep(l);
  // r.agentCur = agentCur ?? '';
  // return r;
}

type BicChangeFunction = (x: BillInvoiceLineItem, c: string | null | undefined) => BillInvoiceLineItem;

function billOrInvoiceCurrencyChangeWorker(
  cur: string | null | undefined,
  newCur: string | null | undefined,
  oldCur: string | null | undefined,
  i: number,
  availableCurrenciesParam: Array<string>,
  cur1Param: string | null | undefined,
  cur2Param: string | null | undefined,
  exRate1Param: number | null | undefined,
  exRate2Param: number | null | undefined,
  primaryCurrency: string | null | undefined,
  lineItemsParam: Array<BillInvoiceLineItem>,
  curChangeFnc: BicChangeFunction
) {
  let cur1 = cur1Param;
  let cur2 = cur2Param;
  let exRate1 = exRate1Param;
  let exRate2 = exRate2Param;
  let availableCurrencies = availableCurrenciesParam;

  const lineItems = lineItemsParam;
  lineItems.forEach((x, ind) => {
    if (ind !== i) {
      return;
    }

    curChangeFnc(x, newCur);
  });

  if (!!oldCur && oldCur !== primaryCurrency) {
    const othersUseOldCurrency = cur === oldCur || lineItems.some((x, ind) => x.cur === oldCur || x.agtCur === oldCur);

    if (!othersUseOldCurrency) {
      availableCurrencies = availableCurrencies.filter(x => x !== oldCur);
      if (cur1 === oldCur) {
        cur1 = cur2;
        exRate1 = exRate2;
        cur2 = null;
        exRate2 = 0;
      }
      if (cur2 === oldCur) {
        cur2 = null;
        exRate2 = 0;
      }
    }
  }

  if (!!newCur && newCur !== primaryCurrency) {
    if (!availableCurrencies.some(x => x === newCur)) {
      availableCurrencies.push(newCur);
    }

    if (cur1 !== newCur && cur2 !== newCur) {
      if (_.isEmpty(cur1)) {
        cur1 = newCur;
        exRate1 = 0;
      } else {
        cur2 = newCur;
        exRate2 = 0;
      }
    }
  }

  return { invCur1: cur1 ?? '', invCur2: cur2 ?? '', exRate1: exRate1 ?? 0, exRate2: exRate2 ?? 0, availableCurrencies, lineItems };
}

/**
 * Utility function to process Bill or Invoice line item currency change. It keeps track of all currencies in line items to make sure max 3 currencies are used
 * After analyzing all line items it returns which are the currencies in use, which is currency 1 and 2 and matching exchange rate
 * In case currency 1 is removed (not used any more) it moves currency 2 to currency 1
 *
 * @export
 * @param {(string|null|undefined)} cur
 * @param {(string|null|undefined)} newCur
 * @param {(string|null|undefined)} oldCur
 * @param {number} i
 * @param {Array<string>} availableCurrencies
 * @param {(string|null|undefined)} cur1Param
 * @param {(string|null|undefined)} cur2Param
 * @param {(number|null|undefined)} exRate1Param
 * @param {(number|null|undefined)} exRate2Param
 * @param {(string|null|undefined)} primaryCurrency
 * @param {Array<BillInvoiceLineItem>} lineItems
 * @return {*} 
 */
export function handleBillOrInvoiceLineItemCurrencyChange(
  cur: string,
  newCur: string | null | undefined,
  oldCur: string | null | undefined,
  i: number,
  availableCurrencies: Array<string>,
  cur1Param: string | null | undefined,
  cur2Param: string | null | undefined,
  exRate1Param: number | null | undefined,
  exRate2Param: number | null | undefined,
  primaryCurrency: string | null | undefined,
  lineItems: Array<BillInvoiceLineItem>
) {
  return billOrInvoiceCurrencyChangeWorker(cur, newCur, oldCur, i, availableCurrencies, cur1Param, cur2Param, exRate1Param, exRate2Param, primaryCurrency, lineItems, applyLineItemCurrencyChange);
}


/**
 * Utility function for Bills and Invoices. Calculates amount in header currency
 *
 * @export
 * @param {(number|null|undefined)} rate
 * @param {(number|null|undefined)} qty
 * @param {(string|null|undefined)} cur
 * @param {(string|null|undefined)} headerCurrency
 * @param {(string|null|undefined)} primaryCurrency
 * @param {(string|null|undefined)} cur1
 * @param {(string|null|undefined)} cur2
 * @param {(string|null|undefined)} cur3
 * @param {(string|null|undefined)} cur4
 * @param {(number|null|undefined)} exRate1
 * @param {(number|null|undefined)} exRate2
 * @param {(number|null|undefined)} exRate3
 * @param {(number|null|undefined)} exRate4
 * @return {*} 
 */
export function calculateHeaderCurrencyAmount(
  rate: number | null | undefined,
  qty: number | null | undefined,
  cur: string | null | undefined,
  headerCurrency: string | null | undefined,
  primaryCurrency: string | null | undefined,
  cur1: string | null | undefined,
  cur2: string | null | undefined,
  cur3: string | null | undefined,
  cur4: string | null | undefined,
  exRate1: number | null | undefined,
  exRate2: number | null | undefined,
  exRate3: number | null | undefined,
  exRate4: number | null | undefined,
) {
  let amount = null;
  if (!qty || !rate || !cur) {
    return amount;
  }

  if (cur === headerCurrency) {
    amount = qty * rate;
  } else {
    if (headerCurrency === primaryCurrency) {
      if (cur === cur1 && !!exRate1) {
        amount = qty * rate * exRate1;
      }
      if (cur === cur2 && !!exRate2) {
        amount = qty * rate * exRate2;
      }
      if (cur === cur3 && !!exRate3) {
        amount = qty * rate * exRate3;
      }
      if (cur === cur4 && !!exRate4) {
        amount = qty * rate * exRate4;
      }
    } else {
      // const headerToPrimaryRate = headerCurrency === cur1 ? exRate1 : exRate2;
      let headerToPrimaryRate = 1;
      if (headerCurrency === cur1) headerToPrimaryRate = exRate1 ?? 0;
      if (headerCurrency === cur2) headerToPrimaryRate = exRate2 ?? 0;
      if (headerCurrency === cur3) headerToPrimaryRate = exRate3 ?? 0;
      if (headerCurrency === cur4) headerToPrimaryRate = exRate4 ?? 0;

      if (headerToPrimaryRate !== 0) {
        const amountInPrimary = calculatePrimaryCurrencyAmount(rate, qty, cur, primaryCurrency, cur1, cur2, exRate1, exRate2);
        if (amountInPrimary) {
          amount = amountInPrimary / headerToPrimaryRate;
        }
      }
    }
  }

  return amount;
}

/**
 *Utility function for Bills and Invoices. Calculates amount in primary currency
 *
 * @export
 * @param {(number|null|undefined)} rate
 * @param {(number|null|undefined)} qty
 * @param {(string|null|undefined)} cur
 * @param {(string|null|undefined)} primaryCurrency
 * @param {(string|null|undefined)} cur1
 * @param {(string|null|undefined)} cur2
 * @param {(number|null|undefined)} exRate1
 * @param {(number|null|undefined)} exRate2
 * @return {*} 
 */
function calculatePrimaryCurrencyAmount(
  rate: number | null | undefined,
  qty: number | null | undefined,
  cur: string | null | undefined,
  primaryCurrency: string | null | undefined,
  cur1: string | null | undefined,
  cur2: string | null | undefined,
  exRate1: number | null | undefined,
  exRate2: number | null | undefined
) {
  let amount = null;

  if (!!qty && !!rate && !!cur) {
    if (cur === primaryCurrency) {
      return qty * rate;
    }
    if (cur === cur1 && !!exRate1) {
      amount = qty * rate * exRate1;
    }
    if (cur === cur2 && !!exRate2) {
      amount = qty * rate * exRate2;
    }
  }

  return amount;
}


/**
 * Utility function to calculate amount for currency in primary currency. It is used for calculation for Bills and Invoices
 *
 * @export
 * @param {(number|null|undefined)} amountParam
 * @param {(string|null|undefined)} cur
 * @param {(string|null|undefined)} primaryCurrency
 * @param {(string|null|undefined)} cur1
 * @param {(string|null|undefined)} cur2
 * @param {(string|null|undefined)} cur3
 * @param {(string|null|undefined)} cur4
 * @param {(number|null|undefined)} exRate1
 * @param {(number|null|undefined)} exRate2
 * @param {(number|null|undefined)} exRate3
 * @param {(number|null|undefined)} exRate4
 * @return {*} 
 */
export function calculatePrimaryCurrencyAmountFor(
  amountParam: number | null | undefined,
  cur: string | null | undefined,
  primaryCurrency: string | null | undefined,
  cur1: string | null | undefined,
  cur2: string | null | undefined,
  cur3: string | null | undefined,
  cur4: string | null | undefined,
  exRate1: number | null | undefined,
  exRate2: number | null | undefined,
  exRate3: number | null | undefined,
  exRate4: number | null | undefined,
): number {
  let amount = null;

  if (!!amountParam && !!cur) {
    if (cur === primaryCurrency) {
      return amountParam;
    }
    if (cur === cur1 && !!exRate1) {
      amount = amountParam * exRate1;
    }
    if (cur === cur2 && !!exRate2) {
      amount = amountParam * exRate2;
    }
    if (cur === cur1 && !!exRate3) {
      amount = amountParam * exRate3;
    }
    if (cur === cur2 && !!exRate4) {
      amount = amountParam * exRate4;
    }
  }

  return amount ?? 0;
}

/**
 * Utility function to calculate CrDb item amount based on its type and if it is direct shipment or not
 *
 * @export
 * @param {number} value
 * @param {(number|null)} type
 * @param {number} ps
 * @param {boolean} direct
 * @return {*} 
 */
export function calculateCrDbValues(value: number, type: number, ps: number | null, direct: boolean) {
  let amount = 0;
  let profitShare = 0;
  if (direct === undefined) {
    const err = new Error();
    // window.console.log(err.stack);
    // window.console.log(type, value, ps, direct);
    // window.console.log('------------');
  }

  if (!direct) {
    switch (type) {
      case 5:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 15:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = -value + profitShare;
        return { amount, profitShare };
      case 27:
        profitShare = ps ? ps * value / 100.0 : 0;
        return { amount: profitShare, profitShare };
      case 37:
        profitShare = ps ? ps * value / -100.0 : 0;
        return { amount: profitShare, profitShare };
      case 7:
        profitShare = ps ? ps * value / 100.0 : 0;
        return { amount: profitShare, profitShare };
      case 17:
        profitShare = ps ? ps * value / -100.0 : 0;
        return { amount: profitShare, profitShare };
      case 8:
      case 18:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 13:
      case 14:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 12:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 3:
      case 4:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = -value + profitShare;
        return { amount, profitShare };
      case 2:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = -value + profitShare;
        return { amount, profitShare };
      case 11:
        profitShare = ps ? ps * value / -100.0 : 0;
        return { amount: profitShare, profitShare };
      case 1:
        profitShare = ps ? ps * value / 100.0 : 0;
        return { amount: profitShare, profitShare };
      default:
        return { amount: 0, profitShare: 0 };
    }
  } else {
    switch (type) {
      case 5:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 15:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = -value + profitShare;
        return { amount, profitShare };
      case 27:
        profitShare = ps ? ps * value / -100.0 : 0;
        return { amount: profitShare, profitShare };
      case 37:
        profitShare = ps ? ps * value / 100.0 : 0;
        return { amount: profitShare, profitShare };
      case 7:
        profitShare = ps ? ps * value / -100.0 : 0;
        return { amount: profitShare, profitShare };
      case 17:
        profitShare = ps ? ps * value / 100.0 : 0;
        return { amount: profitShare, profitShare };
      case 8:
      case 18:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 11:
        profitShare = ps ? ps * value / 100.0 : 0;
        return { amount: profitShare, profitShare };
      case 1:
        profitShare = ps ? ps * value / -100.0 : 0;
        return { amount: profitShare, profitShare };
      case 13:
      case 14:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 3:
      case 4:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = -value + profitShare;
        return { amount, profitShare };
      case 12:
        profitShare = ps ? ps * value / 100.0 : 0;
        amount = value + profitShare;
        return { amount, profitShare };
      case 2:
        profitShare = ps ? ps * value / -100.0 : 0;
        amount = -value + profitShare;
        return { amount, profitShare };
      default:
        return { amount: 0, profitShare: 0 };
    }
  }
}

/**
 * Get number of decimal places for currency. It checks currency in globally available list of currencies which are initialized during login
 *
 * @export
 * @param {string} currency
 * @return {*} 
 */
export function getDecimal(currency: string) {
  const currencies = getCurrencies();
  if (!!currencies && currencies.length > 0) {
    const c = currencies.find(x => x.currency === currency);
    return !!c ? c.decimal : 0;
  }

  return 0;
}

/**
 * Utility function to calculate amount in currency. It checks currency decimal places property and formats (rounds) number accordingly
 *
 * @export
 * @param {(number|null|undefined)} a
 * @param {(string|null|undefined)} c
 * @return {*} 
 */
export function getValueInCurrency(a: number | null | undefined, c: string | null | undefined): number {
  if (!c || a === null || a === undefined) {
    return 0;
  }

  const decimal = getDecimal(c);

  return parseFloat(a.toFixed(decimal));
}


/**
 * Make trackable listitem(or detaillist) in copy mode on load
 *
 * @export
 * @param {any[]} copyApiResult
 * @param {any} resultList
 * @param {any} elementType
 */
export function makeTrackableListItemInCopyMode(copyApiResult: any[], resultList: any, elementType: any) {
  let propertyValue = Object.create(null);
  copyApiResult.forEach(x => {
    let newElement = new elementType();
    newElement.trackable = true;
    const elementProp = Object.getOwnPropertyNames(newElement)
    const trackaProp = elementProp.filter(name => !Object.getOwnPropertyDescriptor(newElement, 'noTrackProps')?.value.includes(name))
    trackaProp.forEach((y) => {
      propertyValue = Object.getOwnPropertyDescriptor(x, y)?.value
      Object.defineProperty(newElement, y, { value: propertyValue })
    })
    resultList.push(newElement);
  });
}

/**
 * Check if object or string is null or empty or whitespace
 *
 * @export
 * @param {string} str
 * @param {boolean} isWhiteSpaceCheck
 * @return {boolean} 
 */
export function isNullOrEmpty(str: string | null, isWhiteSpaceCheck = true): boolean {

  if (!str)
    return true;

  if (isWhiteSpaceCheck)
    // return !str.trim();
    return typeof str === 'string' ? !str.trim() : false;
  else
    return false;
}

/**
 * Tries to set caret (cursor) of given HTML input element to given position
 *
 * @export
 * @param {HTMLInputElement} el
 * @param {number} caretPos
 * @return {*} 
 */
export function setCaretPosition(el: HTMLInputElement, caretPos: number) {
  // this is used to not only get "focus", but
  // to make sure we don't have it everything -selected-
  // (it causes an issue in chrome, and having it doesn't hurt any other browser)
  // eslint-disable-next-line no-self-assign
  el.value = el.value;

  if (el !== null) {
    const elAsAny = el as any;
    if (elAsAny.createTextRange) {
      const range = elAsAny.createTextRange();
      range.move('character', caretPos);
      range.select();
      return true;
    }
    // (el.selectionStart === 0 added for Firefox bug)
    if (el.selectionStart || el.selectionStart === 0) {
      el.focus();
      el.setSelectionRange(caretPos, caretPos);
      return true;
    }

    el.focus();
    return false;
  }
}


/**
 * Checks whether array's last element is empty. Used for tables to not allow new line item to show if the last one is empty.
 * It is possible to define additional values that are treated as empty ones (for examnple: 'N/A') and properties in array to skip for checking
 *
 * @export
 * @param {*} arr
 * @param {(Array<any>|null)} additionalEmptyValues
 * @param {(Array<string>|null)} propertiesToSkip
 * @return {*} 
 */
export function isLastLineItemEmpty(arr: any, additionalEmptyValues: Array<any> | null = null, propertiesToSkip: Array<string> | null = null) {
  return Array.isArray(arr) && arr.length > 0 && isEveryPropertyEmpty(arr[arr.length - 1], additionalEmptyValues, propertiesToSkip);
}

// [230410] improve function
// export function isEveryPropertyEmpty(obj:any, additionalEmptyValues:Array<any>|null = null, propertiesToSkip:Array<string>|null = null) {
//   type CheckFunctor = (x:any) => boolean;

//   const isNullOrEmpty = (str: string) => str === null || str.match(/^ *$/) !== null;

//   const additionalChecks = additionalEmptyValues ?? [];

//   const emptyCheck:CheckFunctor = x => {
//     if (_.isString(x)) {
//       return isNullOrEmpty(x) || additionalChecks.some(a => a === x);
//     }

//     if (_.isNumber(x)) {
//       return !x || x === 0 || additionalChecks.some(a => a === x);
//     }

//     return !x || additionalChecks.some(a => a === x);
//   };

//   return !!propertiesToSkip && Array.isArray(propertiesToSkip) && propertiesToSkip.length > 0
//     ? !obj || _.values(_.omit(obj, propertiesToSkip)).every(emptyCheck)
//     : !obj || _.values(obj).every(emptyCheck);
// }


/**
 * Checks whether object is empty. Used for tables to not allow new line item to show if the last one is empty.
 * It is possible to define additional values that are treated as empty ones (for examnple: 'poNo':'N/A') and properties in array to skip for checking
 *
 * @export
 * @param {*} obj
 * @param {(any|null)} additionalEmptyValues
 * @param {(Array<string>|null)} propertiesToSkip
 * @return {*} 
 */
export function isEveryPropertyEmpty(obj: any, additionalEmptyValues: any | null = null, propertiesToSkip: Array<string> | null = null) {
  if (!obj) return true;
  type CheckFunctor = (x: any) => boolean;
  let testObj = _.cloneDeep(obj);
  let specialObj: any = [];
  if (!!propertiesToSkip && Array.isArray(propertiesToSkip) && propertiesToSkip.length > 0)
    testObj = _.omit(obj, propertiesToSkip)

  if (!!additionalEmptyValues) {
    specialObj = additionalEmptyValues;
    const keys = Object.keys(additionalEmptyValues)
    for (let i = 0; i < keys.length; i++) {
      const keyName = keys[i];
      delete testObj[keyName]
    }
  }

  const emptyCheck: CheckFunctor = x => {
    if (_.isString(x)) {
      return isNullOrEmpty(x);
    }

    else if (_.isNumber(x)) {
      return !x || x === 0;
    }

    return !x;
  };

  const emptyCheckSpecial: CheckFunctor = x => {
    if (_.isString(x)) {
      return isNullOrEmpty(x) || specialObj[x] === obj[x];
    }

    else if (_.isNumber(x)) {
      return !x || x === 0 || specialObj[x] === obj[x];
    }

    return !x || specialObj[x] === obj[x];
  };

  return _.values(testObj).every(emptyCheck) && _.keys(specialObj).every(emptyCheckSpecial);
}


/**
 * Utility function to format amount as string to given number of decimal places
 *
 * @export
 * @param {*} amount
 * @param {number} [decimalCount=2]
 * @param {string} [decimals='.']
 * @param {string} [thousands=',']
 * @return {*} 
 */
export function formatAmount(amount: any, decimalCount = 2, decimal = '.', thousands = ',') {
  try {
    decimalCount = Math.abs(decimalCount);
    decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

    const negativeSign = amount < 0 ? "-" : "";

    let i: any = parseInt(amount = Math.abs(Number(amount) || 0).toFixed(decimalCount)).toString();
    let j = (i.length > 3) ? i.length % 3 : 0;

    return negativeSign + (j ? i.substr(0, j) + thousands : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) + (decimalCount ? decimal + Math.abs(amount - i).toFixed(decimalCount).slice(2) : "");
  } catch (e) {
    // console.log(e);
    return '';
  }
}

export function isElementFocusable(el: HTMLElement | null): boolean {
  if (!el) {
    return false;
  }

  const elAsAny = el as any;
  return !elAsAny.disabled && !!elAsAny.focus;
}

/**
 *Extract query parameters as key-value pair from history
 *
 * @export
 * @param {History} history
 */
export function getQueryParamObject(history: History) {
  const urlSearchParams = new URLSearchParams(history?.location?.search);
  return Object.fromEntries(urlSearchParams.entries());
}

// 260821 hykim: 미사용 parseJwt 제거 (토큰 클레임을 프론트에서 디코드할 일 없음)

//<pt211201>
export function appendLine(str1: string, str2: string): string {
  let result = str1;
  if (str1) result += '\r\n';
  return result + str2;
}

/**
 *Convert number in inches to cm
 *
 * @export
 * @param {number} num
 */
export function cbmInchToCm(num: number) {
  if (num === null || num === undefined || isNaN(num)) {
    return 0;
  }

  return Math.round(num / 16387.0486 * 100) / 100;
}

/**
 *Convert number in cm to inches
 *
 * @export
 * @param {number} num
 */
export function cbmCmToInch(num: number) {
  if (num === null || num === undefined || isNaN(num)) {
    return 0;
  }
  return Math.round((num * 16387.0486) * 100) / 100;
}


/**
 *Convert number in inches to cm
 *
 * @export
 * @param {number} num
 */
export function lbsInchToCm(num: number) {
  if (num === null || num === undefined || isNaN(num)) {
    return 0;
  }

  return Math.round(num * 1.639507 * 100) / 100;
}

/**
 *Convert number in cm to inches
 *
 * @export
 * @param {number} num
 */
export function lbsCmToInch(num: number) {
  if (num === null || num === undefined || isNaN(num)) {
    return 0;
  }

  return Math.round((num / 1.639507) * 100) / 100;
}



/**
 * Sleep async function
 * @param ms 
 * @returns 
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}


/**
 * 
 * @param str 
 * @param index 
 * @param length 
 * @param replace 
 */
export function replaceAt(str: string, index: number, length: number, replace: string) {
  if (!str)
    return str
  else return str.substring(0, index) + replace + str.substring(index + 1, str.length);
}

/**
 * pt220912: return first line only
 * @param str multiline string
 * @returns 
 */
export function stringGetFirstLine(str: string) {
  if (!str || str.length === 0) return str;
  else {
    const arr = str.match(/^.*$/m) ?? [];
    if (arr.length > 0)
      return arr[0];
    else
      return '';
  }
}

export function stringGetSecondLines(str: string) {
  if (!str || str.length === 0) return str;
  const index = str.indexOf('\n');
  if (index !== -1) {
    return str.substring(index + 1);
  } else {
    return '';
  }
}

export function cbmToCft(cbm: number, retDecimal: number = -1) {
  if (cbm < 0) return 0;
  if (retDecimal > -1) {
    return mathRound(cbm * 35.3147, retDecimal);
  } else {
    return cbm * 35.3147;
  }
}

export function kgsToLbs(kgs: number, retDecimal: number = -1) {
  if (kgs < 0) return 0;
  if (retDecimal > -1) {
    return mathRound(kgs * 2.2046, retDecimal);
  } else {
    return kgs * 2.2046;
  }
}

export function getCbmBackgroundColor(cbm: number, lbs: number) {
  return cbm * 314.26 > lbs ? '#db7100' : undefined;
}

export function left(str: string, n: number) {
  if (!str || str.length <= 0) return '';
  else if (n < str.length) return str.substring(0, n);
  else return str;
}

function filterListBySecTask(filterList: IfmsConfigMenuItem[], secTask: string): IfmsConfigMenuItem[] {
  return filterList = filterList.filter(x => {
    if (x.fmsSecTask !== secTask) {
      x.items = x.items?.filter(f => f.fmsSecTask !== secTask);
      return x;
    }
    else
      return null;
  });
}

/**
 * e-AWB 활성 여부. 데스크톱 FmsMaintenanceAirOption.EnableEawb(=GlsHkId>0||CciId>0)과 동일하게
 * EDI 계정 id 를 우선 기준으로 본다. id 는 백엔드 Detail("OceanAir")이 GetEawbInfoAsync로 채워 내려줌.
 * (저장 직후 재조회 전 등 id 가 아직 없을 수 있어 sender/agent 텍스트를 폴백으로 둠 — 둘은 동일 원격 계정에서 옴)
 */
export function isEawbEnabled(suntekSettings: any): boolean {
  const oa = suntekSettings?.oceanAir;
  return (oa?.glsHkId ?? 0) > 0 || (oa?.cciId ?? 0) > 0 || !!oa?.glsHkSenderId || !!oa?.cciAgentCode;
}

/** e-AWB 브로커 타입 (GlsHk/Cci/0=없음). 계정 id 우선, 텍스트 폴백. */
export function eawbBrokerType(suntekSettings: any): number {
  const oa = suntekSettings?.oceanAir;
  if ((oa?.glsHkId ?? 0) > 0 || !!oa?.glsHkSenderId) return EawbBrokerType.GlsHk;
  if ((oa?.cciId ?? 0) > 0 || !!oa?.cciAgentCode) return EawbBrokerType.Cci;
  return 0;
}

export function MenuFilterGrents(filterList: IfmsConfigMenuItem[], grents: SecurityGrant[] | null | undefined, appContext: any): IfmsConfigMenuItem[] {
  filterList = filterList.map(x => ({ ...x }));

  // setup
  let disableMenu: IfmsConfigMenuItem | undefined;

  // <250131.jhlee - AMS와 ISF의 Enable 항목의 체크 여부와 관계없이 메뉴가 보이도록 수정>
  //AMS Setup
  if (!appContext.suntekSettings?.compliance?.amsEnableAll) {
    // if (!appContext.suntekSettings?.compliance?.amsEnable) {
    //   // filterList = filterList.filter(x => !x.text?.toString() || !x.text.toString().includes('AMS'));
    //   filterList = filterList.filter(x => !x.text?.toString() || !x.text.toString().includes('ACI'));
    // }
    // else {
    //   if (appContext.suntekSettings?.compliance?.amsEnable) {
    //     if (_.isEmpty(appContext.suntekSettings.compliance.amsId1) && _.isEmpty(appContext.suntekSettings.compliance.amsId2) && _.isEmpty(appContext.suntekSettings.compliance.amsId3)) {
    //       disableMenu = filterList.find(x => x.text?.toString().includes('ACI eManifest'));
    //       if (disableMenu) disableMenu.disable = true;
    //       // disableMenu = filterList.find(x => x.text?.toString().includes('AMS'));
    //       // if (disableMenu) disableMenu.disable = true;
    //     }
    //   }
    // }

    // 위에 로직에서 불필요한 부분 제거하고 재정리
    // AMS, ISF 메뉴는 항상 표시되고 ACI eManifest 메뉴는 amsId1,2,3가 없으면 비활성화 처리
    if (_.isEmpty(appContext.suntekSettings.compliance.amsId1) && _.isEmpty(appContext.suntekSettings.compliance.amsId2) && _.isEmpty(appContext.suntekSettings.compliance.amsId3)) {
      disableMenu = filterList.find(x => x.text?.toString().includes('ACI eManifest'));
      if (disableMenu) disableMenu.disable = true;
    }
  }

  //ACI eManifest Setup
  if (!appContext.suntekSettings?.compliance?.amsUseeManifest) {
    filterList = filterList.filter(x => !x.text?.toString() || !x.text.toString().includes('ACI eManifest'));
  } 

  //ISF Setup
  // if (!appContext.suntekSettings?.compliance?.isfEnableAll) {
  //   if (!appContext.suntekSettings?.compliance?.isfEnable) {
  //     filterList = filterList.filter(x => !x.text?.toString() || !x.text.toString().includes('ISF'));
  //   }
  //   else {
  //     if (_.isEmpty(appContext.suntekSettings.compliance.isfId1) && _.isEmpty(appContext.suntekSettings.compliance.isfId2) && _.isEmpty(appContext.suntekSettings.compliance.isfId3)) {
  //       disableMenu = filterList.find(x => x.text?.toString().includes('ISF'));
  //       if (disableMenu) disableMenu.disable = true;
  //     }
  //   }
  // }

  if (!appContext.suntekSettings?.accounting?.useSalesTaxReturn) {
    filterList = filterListBySecTask(filterList, FmsSecTask.AcctSalesTaxReturnList);
  }
  // jioyu - 260223 Suntek Settings에서 Branch 옵션을 Enable하지 않아도 Branch Performance Report 메뉴가 항상 보이도록 변경
  // if (!appContext.suntekSettings?.company?.enableBranch) {
  //   filterList = filterListBySecTask(filterList, FmsSecTask.OPRptBranchPerform);
  // }
  //<240529.dwshin - SiS만 가능하던것 사용할 수 있게 수정 됨. 2024-01-12>
  //// if(appContext.user?.id !== getFmsGlobal().SUNTEK_ID){
  // if(!appContext.user?.isSuntekAdmin) {
  //   filterList = filterListBySecTask(filterList, FmsSecTask.AcctBadDebtExpense);
  // }

  // AMS/ISF Log List는 CMS User만 접근 가능
  if (!appContext.user?.isSuntekAdmin) {
    filterList = filterList.filter(x => !x.text?.toString() || !x.text.toString().includes('AMS/ISF Log List'));
  }

  // security
  if (grents === null || grents === undefined) return filterList;

  return filterList.filter(job => {
    if (!job.fmsSecTask
      || grents?.find(g => g.securityTaskCode === job.fmsSecTask) === undefined
      || grents?.find(g => g.securityTaskCode === job.fmsSecTask && g.grantRead === true)) {
      // sub job (level 1 filter)
      job.items = job.items?.filter(sjob => !sjob.fmsSecTask
        || grents?.find(g => g.securityTaskCode === sjob.fmsSecTask) === undefined
        || grents?.find(g => g.securityTaskCode === sjob.fmsSecTask && g.grantRead === true));

      // level 0 컴포넌트에 action이 있거나,
      // 하위 아이템이 있는 경우에만 메뉴 보여주도록 함.
      //if(job.link || job.popupComponent || job.metadata || (job.items && job.items.length > 0))
      //2305233 위 기능 주석처리
      if (job.nonItemThenHide && (job.items?.length ?? 0) <= 0)
        return null;
      else
        return job;
    }
    //240919 - mskang > Deposit / Undo Deposit은 Grant가 Deposit/UndoDeposit일때 뿐 아니라 Check Clear/Undo Check Clear일때도 활성화 되어야함. 
    //Deposit/UndoDeposit Grant가 false여도 Check Clear/Undo Check Clear Grant가 true면 메뉴에 나타나야 한다.
    else if ((job.fmsSecTask === FmsSecTask.AcctDeposit || job.fmsSecTask === FmsSecTask.AcctDepositClear) &&
      (grents?.find(g => g.securityTaskCode === FmsSecTask.AcctDeposit) && grents?.find(g => g.securityTaskCode === FmsSecTask.AcctDepositClear && g.grantRead === true))) {
      return job;
    }
    else if ((job.fmsSecTask === FmsSecTask.AcctUndoDeposit || job.fmsSecTask === FmsSecTask.AcctUndoDepositClear) &&
      (grents?.find(g => g.securityTaskCode === FmsSecTask.AcctUndoDeposit) && grents?.find(g => g.securityTaskCode === FmsSecTask.AcctUndoDepositClear && g.grantRead === true))) {
      return job;
    }
    else if (job.fmsSecTask === FmsSecTask.AcctAging && grents?.find(g => (g.securityTaskCode === FmsSecTask.AcctAging && g.grantRead === true))) {
      return job;
    }
    else
      return null;
  });
}



const storageDetailKey = new Map<string, string>([
  [JobNames.Invoice, "/accounting/invoice"],
  [JobNames.Bill, "/accounting/bill"],
  [JobNames.CrDb, "/accounting/crdb"],
  [JobNames.GInvo, "/accounting/acGeneralInvoice"],
  [JobNames.GBill, "/accounting/acGeneralBill"],
]);

export function getJobNamesByCurrentUrl(map: any, searchValue: any) {
  for (let [key, value] of map.entries()) {
    if (value === searchValue)
      return key as JobNames;
  }
  return JobNames.None;
}

export function getListItemByJobName(jobName: JobNames) {
  switch (jobName) {
    case JobNames.OIMbl:
      return OiMblListItem;
    case JobNames.OIHbl:
      return OiHblListItem;
    case JobNames.AIMbl:
      return AiMblListItem;
    case JobNames.AIHbl:
      return AiHblListItem;
    case JobNames.OEMbl:
      return OeMblListItem;
    case JobNames.OEHbl:
      return OeHblListItem;
    case JobNames.OEBook:
      return OeBookingListItem;
    case JobNames.AEMbl:
      return AeMblListItem;
    case JobNames.AEHbl:
      return AeHblListItem;
    case JobNames.OtherOP:
      return OtherOperationListItem;
    case JobNames.WHRcpt:
      return WhReciptsListItem;
    case JobNames.WHRcv:
      return WhRcvListItem;
    case JobNames.WHShip:
      return WhShipListItem;
    case JobNames.Company:
      return CodeCompanyListItem;
    case JobNames.BillPayment:
      return BillPaymentListItem;
    case JobNames.OIMPims:
    case JobNames.OEMPims:
    case JobNames.AIMPims:
    case JobNames.AEMPims:
      return PimsBookingListItem;
    case JobNames.OIHPims:
    case JobNames.OEHPims:
    case JobNames.AIHPims:
    case JobNames.AEHPims:
      return PimsVanningListItem;
    case JobNames.BatchRecog:
      return BatchRecogListItem;
    case JobNames.Invoice:
    case JobNames.GInvo:
    case JobNames.OpInvoice:
    case JobNames.WHInvoice:
    case JobNames.WHStorageInv:
      return InvoiceListItem;
    case JobNames.Bill:
    case JobNames.GBill:
    case JobNames.OpBill:
    case JobNames.WHBill:
      return BillListItem;
    case JobNames.CrDb:
    case JobNames.OpCrDb:
    case JobNames.WHCrDb:
      return CrDbListItem;
    case JobNames.SlipJournal:
      return JournalLedgerListItem;

    case JobNames.MyEvent:
      return MyEventListItem;

    case JobNames.ISF:
      return IsfListItem;
    
    case JobNames.RptCustomerShipment:
      return CustomerShipmentListItem;
    default:
      return undefined;

  }
}

export function GetCompanyNameNew(vID: number, vComName: string, vComType: string = 'C', vIgnoreBlank: boolean = false): string {
  let result = '';

  if (vID > 0) {
    if (!_.isEmpty(vComName))
      result = vComName.length > 0 ? vComName.substring(0, 50) : vComName;
    else
      result = '** Not Registered Entity **';
  }
  else {
    result = vIgnoreBlank ? '' : '** Blank Entity **';
  }
  return result;
}

export function getTooltipValue(value: string, jobName: JobNames, customLabel: string = '') {
  let result = ''

  if (_.isEmpty(value)) return result;

  if (!_.isEmpty(customLabel))
    result = `${customLabel} : ${value}`
  else {
    switch (jobName) {
      case JobNames.OIMbl:
      case JobNames.OEMbl:
        result = `MBL # : ${value}`
        break;
      case JobNames.AIMbl:
      case JobNames.AEMbl:
        result = `MAWB # : ${value}`
        break;
      case JobNames.OIHbl:
      case JobNames.OEHbl:
        result = `HBL # : ${value}`
        break;
      case JobNames.AIHbl:
      case JobNames.AEHbl:
        result = `HAWB # : ${value}`
        break;
      case JobNames.OEBook:
        result = `Booking # : ${value}`
        break;
      case JobNames.OtherOP:
        result = `Ref # : ${value}`
        break;
      case JobNames.WHRcpt:
        result = `Receipt # : ${value}`
        break;
      case JobNames.WHRcv:
        result = `Receiving ID : ${value}`
        break;
      case JobNames.WHShip:
        result = `Shipping ID : ${value}`
        break;
      case JobNames.Invoice:
      case JobNames.WHInvoice:
      case JobNames.WHStorageInv:
      case JobNames.GInvo:
      case JobNames.Bill:
      case JobNames.WHBill:
      case JobNames.GBill:
      case JobNames.CrDb:
      case JobNames.WHCrDb:
        result = `${value}`
        break;
      case JobNames.SeaQuotation:
      case JobNames.OIQuotation:
      case JobNames.OEQuotation:
      case JobNames.AirQuotation:
      case JobNames.AIQuotation:
      case JobNames.AEQuotation:
      case JobNames.OtQuotation:
        result = `Quotation # : ${value}`
        break;
      case JobNames.OILoad:
        result = `Load Plan # : ${value}`
        break;
      case JobNames.OELoad:
        result = `Container # : ${value}`
        break;
      case JobNames.Company:
        result = `Company Name : ${value}`
        break;
      case JobNames.SalesTaxReturn:
        result = `Ref # : ${value}`
        break;
      case JobNames.PayrollSummary:
      case JobNames.Payroll2:
        result = `Ref # : ${value}`
        break;
    }
  }
  return result;
}

export function getSecTaskByJobName(jobName: JobNames) {
  switch (jobName) {
    case JobNames.OIMbl:
      return FmsSecTask.OIMbl;
    case JobNames.OIHbl:
      return FmsSecTask.OIHbl;
    case JobNames.AIMbl:
      return FmsSecTask.AIMbl;
    case JobNames.AIHbl:
      return FmsSecTask.AIHbl;
    case JobNames.OEMbl:
      return FmsSecTask.OEMbl;
    case JobNames.OEHbl:
      return FmsSecTask.OEHbl;
    case JobNames.OEBook:
      return FmsSecTask.OEBook;
    case JobNames.AEMbl:
      return FmsSecTask.AEMbl;
    case JobNames.AEHbl:
      return FmsSecTask.AEHbl;
    case JobNames.OtherOP:
    case JobNames.OtherContainer:
      return FmsSecTask.OtherOP;
    case JobNames.WHRcpt:
      return FmsSecTask.WHRcpt;
    case JobNames.WHRcv:
      return FmsSecTask.WHRcv;
    case JobNames.WHShip:
      return FmsSecTask.WHShip;
    case JobNames.Company:
      return FmsSecTask.CodeCompany;
    case JobNames.OIContainer:
      return FmsSecTask.OIContainer;
    case JobNames.OEContainer:
      return FmsSecTask.OEContainer;
    case JobNames.OtherContainer:
      return FmsSecTask.OtherOP;
    case JobNames.OIQuotation:
      return FmsSecTask.OIQuot;
    case JobNames.OEQuotation:
      return FmsSecTask.OEQuot;
    case JobNames.AIQuotation:
      return FmsSecTask.AIQuot;
    case JobNames.AEQuotation:
      return FmsSecTask.AEQuot;
    case JobNames.OtQuotation:
      return FmsSecTask.OTQuot;
    case JobNames.SalesTaxReturn:
      return FmsSecTask.AcctSalesTaxReturnList;
    case JobNames.Payroll2:
      return FmsSecTask.AcctPayroll;
    case JobNames.BankTransfer:
      return FmsSecTask.AcctBankTransfer;
    case JobNames.BankRecon:
      return FmsSecTask.AcctBankRecon;
    case JobNames.CustPayment:
      return FmsSecTask.AcctCustPayment;
    case JobNames.BillPayment:
      return FmsSecTask.AcctBillPayment;
    case JobNames.AgntPayment:
      return FmsSecTask.AcctAgntPayment;
    case JobNames.CCPayment:
      return FmsSecTask.AcctCCPayment;
    case JobNames.Invoice: 
      return FmsSecTask.AcctInvoiceList;
    case JobNames.Bill: 
      return FmsSecTask.AcctBillList;
    case JobNames.CrDb: 
      return FmsSecTask.AcctCrDbList;
    case JobNames.GInvo: 
      return FmsSecTask.AcctGInvoList;
    case JobNames.GBill: 
      return FmsSecTask.AcctGBillList;
    case JobNames.WHStorageInv: 
       return FmsSecTask.WHStorageInv;
    case JobNames.OILoad: 
      return FmsSecTask.OITruckLoad;

    default:
      return undefined;
  }
}

// Edit/Delete 권한이 필요한 경우 사용되는 함수
// MBL List View > New HBL 혹은 Billings Tab > New Invoice/Bill/CrDb 같은 Add 권한 검사가 필요한 경우에도 사용
export function findSecurityTaskWithBranch(appContext: IApplicationContext, taskCode: string, rowBranch: string, checkGrantAdd:boolean = false): SecurityGrant {
  const found = appContext.securityGrants?.find(x => x.securityTaskCode === taskCode);
  if (!found) return new SecurityGrant({ grantRead: true, grantAdd: true, grantEdit: true, grantDel: true });

  const result = new SecurityGrant(found);
  const userBranch = appContext.user?.branch ?? '';

  if (rowBranch !== undefined && rowBranch !== '' && userBranch !== rowBranch) {
    const fmsGlobal = getFmsGlobal();
    const blockEditDelAdd = fmsGlobal.NoEditAllBranchData ?? false;

    if (checkGrantAdd) result.grantAdd = result.grantAdd && !blockEditDelAdd;
    result.grantEdit = result.grantEdit && !blockEditDelAdd;
    result.grantDel = result.grantDel && !blockEditDelAdd;
  }

  return result;
}

export function findSecurityTask(appContext: IApplicationContext, taskCode: string): SecurityGrant {
  let result = appContext.securityGrants?.find(x => x.securityTaskCode === taskCode);
  return result ? result : new SecurityGrant({ grantRead: true, grantAdd: true, grantEdit: true, grantDel: true });
}

export function getSecBySecTask(appContext: IApplicationContext, taskCode: string, secType: SecType = SecType.Read): boolean {
  const secGrant = findSecurityTask(appContext, taskCode);

  switch (secType) {
    case SecType.Read: return secGrant?.grantRead ?? true;
    case SecType.Add: return secGrant?.grantAdd ?? true;
    case SecType.Edit: return secGrant?.grantEdit ?? true;
    case SecType.Delete: return secGrant?.grantDel ?? true;
    case SecType.YesOrNo: return secGrant?.grantYesNo ?? true;
  }
}

export function hasReadableSecurityTask(appContext: IApplicationContext, taskCodes: any): boolean {
    return appContext.securityGrants?.some( x => taskCodes.includes(x.securityTaskCode) && x.grantRead === true) ?? false;
}

export const stringArraysEqual = (arr1: string[], arr2: string[]) => {
  if (arr1.length !== arr2.length) return false;
  arr1.sort();
  arr2.sort();
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
};

export async function duplicateCell(checkData: any, data: any, appContext: IApplicationContext, listItemType: string, showGlobalOkDialog: any,
  jobName: JobNames, checkProp: string, checkProp2: string|null = null ) {

  let duplicateFieldText = '';
  let isDuplicate = false;

  if (!data) return isDuplicate;

  let insList = checkData.filter((x: any) => x.isNew === true);
  let chgList = checkData.filter((x: any) => x.hasBeenChanged === true && !x.isNew);
  let focusRow = -1;

  if (insList.length > 0 && data.length > 0) {
    for (let i = 0; i < insList.length; i++) {
      let count = 0;
      let tempRow = -1;
      for (let j = 0; j < data.length; j++) {
        if ((checkProp2 === null && insList[i][checkProp].toUpperCase() === data[j][checkProp].toUpperCase())
          || (checkProp2 != null && insList[i][checkProp].toUpperCase() === data[j][checkProp].toUpperCase() && (insList[i][checkProp2].toUpperCase() === data[j][checkProp2].toUpperCase()))) {
          if (count === 0) { tempRow = j; }
          count++;
          if (count > 1) {
            if (focusRow === -1) focusRow = tempRow;
            break;
          }
        }
      }
      if (count > 1) {
        duplicateFieldText += `'${insList[i][checkProp]}${(checkProp2 != null ? ', ' + insList[i][checkProp2] : '')}' is a duplicate value.\r\n`;
        isDuplicate = true;
      }
    }
  }

  if (chgList.length > 0 && data.length > 0) {
    for (let i = 0; i < chgList.length; i++) {
      let count = 0;
      let tempRow = -1;
      for (let j = 0; j < data.length; j++) {
        if ((checkProp2 === null && chgList[i][checkProp].toUpperCase() === data[j][checkProp].toUpperCase())
          || (checkProp2 != null && chgList[i][checkProp].toUpperCase() === data[j][checkProp].toUpperCase() && (chgList[i][checkProp2].toUpperCase() === data[j][checkProp2].toUpperCase()))) {
          if (!data[j].isNew && data[j].hasBeenChanged === true && tempRow === -1) { tempRow = j; }
          count++;
          if (count > 1) {
            if (focusRow === -1) focusRow = tempRow;
            break;
          }
        }
      }
      if (count > 1) {
        duplicateFieldText += `'${chgList[i][checkProp]}${checkProp2 != null ?  ', ' + chgList[i][checkProp2] : ''}' is a duplicate value.\r\n`;
        isDuplicate = true;
      }
    }
  }

  if (!isDuplicate) {
    const apiResult = await appContext.api.checkDuplicatedKeys(jobName, listItemType, insList, chgList);
    if (apiResult && apiResult.success && apiResult.message) {
      duplicateFieldText = `${apiResult.message}`;
      isDuplicate = true;
    } else if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog('Could not check duplication.\r\nPlease try later.');
      return true;
    }
  }
  else
    await showGlobalOkDialog(`${duplicateFieldText}Please try after correcting duplication.`);

  return isDuplicate;
}


export async function deleteMemo(appContext: IApplicationContext, mode: FormType, id: number, tbName: string){
  if(mode === FormType.ModeAdd && id < 0){
    await appContext.api.deleteMemoWithTbId(id,tbName);
  }
} 

export function CheckRelationAcctOpen(jobId: number, jobName: JobNames, parentJobId: number, hblIds: number[], jobMenu: JobMenuState): { crdbLock: boolean, invoLock: boolean, billLock: boolean, jobTabLock: boolean } {
  const result = { crdbLock: false, invoLock: false, billLock: false, jobTabLock: false };
  if (jobId < 0) return result;

  const parentJobName = getMasterJob(jobName);
  const isHouse = parentJobName !== jobName;
  const jobType = getBLTypeByJobName(jobName);

  const invoEditUrl = `accounting/invoice/edit/${jobName}/${jobId}`;
  const invoAddUrl = `accounting/invoice/add/${jobName}/${jobId}`;
  const billEditUrl = `accounting/bill/edit/${jobName}/${jobId}`;
  const billAddUrl = `accounting/bill/add/${jobName}/${jobId}`;
  const crdbEditUrl = `accounting/crdb/edit/${jobName}/${jobId}`;
  const crdbAddUrl = `accounting/crdb/add/${jobName}/${jobId}`;

  const relevantCategories = ['accounting', 'billings'];
  if (jobType && jobType in jobMenu) {
    relevantCategories.push(jobType);
  }

  if (isHouse) { // House
    const masterCrdbEditUrl = `accounting/crdb/edit/${parentJobName}/${parentJobId}`;
    const masterCrdbAddUrl = `accounting/crdb/add/${parentJobName}/${parentJobId}`;

    for (const category of relevantCategories) {
      if (!jobMenu[category]) continue;

      for (const jobMenuItem of jobMenu[category]) {
        if (jobMenuItem.url.includes(billEditUrl) || jobMenuItem.url.includes(billAddUrl)) {
          //result.billLock = true;
          result.crdbLock = true;
          result.jobTabLock = true;
        }

        if (jobMenuItem.url.includes(invoEditUrl) || jobMenuItem.url.includes(invoAddUrl)) {
          //result.invoLock = true;
          result.crdbLock = true;
          result.jobTabLock = true;
        }

        if (jobMenuItem.url.includes(crdbEditUrl) || jobMenuItem.url.includes(crdbAddUrl)) {
          result.crdbLock = true;
          result.invoLock = true;
          result.billLock = true;
          result.jobTabLock = true;
        }

        if (jobMenuItem.url.includes(masterCrdbEditUrl) || jobMenuItem.url.includes(masterCrdbAddUrl)) {
          result.crdbLock = true;
          result.invoLock = true;
          result.billLock = true;
          result.jobTabLock = true;
        }
      }
    }
  } else { // Master
    for (const category of relevantCategories) {
      if (!jobMenu[category]) continue;

      for (const jobMenuItem of jobMenu[category]) {
        if (jobMenuItem.url.includes(billEditUrl) || jobMenuItem.url.includes(billAddUrl)) {
          //result.billLock = true;
          result.crdbLock = true;
          result.jobTabLock = true;
        }

        if (jobMenuItem.url.includes(invoEditUrl) || jobMenuItem.url.includes(invoAddUrl)) {
          //result.invoLock = true;
          result.crdbLock = true;
          result.jobTabLock = true;
        }

        if (jobMenuItem.url.includes(crdbEditUrl) || jobMenuItem.url.includes(crdbAddUrl)) {
          result.crdbLock = true;
          result.invoLock = true;
          result.billLock = true;
          result.jobTabLock = true;
        }
      }
    }

    if (!isHouse) { // Master
      const houseJobName = getHouseJob(jobName);

      for (const hblId of hblIds) {
        const hblInvoEditUrl = `accounting/invoice/edit/${houseJobName}/${hblId}`;
        const hblInvoAddUrl = `accounting/invoice/add/${houseJobName}/${hblId}`;
        const hblBillEditUrl = `accounting/bill/edit/${houseJobName}/${hblId}`;
        const hblBillAddUrl = `accounting/bill/add/${houseJobName}/${hblId}`;
        const hblCrdbEditUrl = `accounting/crdb/edit/${houseJobName}/${hblId}`;
        const hblCrdbAddUrl = `accounting/crdb/add/${houseJobName}/${hblId}`;

        for (const category of relevantCategories) {
          if (!jobMenu[category]) continue;

          for (const jobMenuItem of jobMenu[category]) {
            if (
              jobMenuItem.url.includes(hblInvoEditUrl) ||
              jobMenuItem.url.includes(hblInvoAddUrl) ||
              jobMenuItem.url.includes(hblBillEditUrl) ||
              jobMenuItem.url.includes(hblBillAddUrl) ||
              jobMenuItem.url.includes(hblCrdbEditUrl) ||
              jobMenuItem.url.includes(hblCrdbAddUrl)
            ) {
              result.crdbLock = true;
            }
          }
        }
      }
    }
  }

  return result;
}

function getUsedShipmentTypeInGrid(costs: OpAcct[], sales: OpAcct[]) {
  let sRetType = "";
  // Check Sales
  for (const opAcct of sales) {
    if (opAcct.void === 0) {
      if (opAcct.chgType === 1 || opAcct.chgType === 6) {
        sRetType = "D";
      }
      else if (opAcct.chgType === 2 || opAcct.chgType === 3 || opAcct.chgType === 4) {
        sRetType = "I";
      }
    }
  }
  if (sRetType === "") {
    // Check Cost
    for (const opAcct of costs) {
      if (opAcct.void === 0) {
        if (opAcct.chgType === 11 || opAcct.chgType === 16) {
          sRetType = "D";
        }
        else if (opAcct.chgType === 12 || opAcct.chgType === 13 || opAcct.chgType === 14) {
          sRetType = "I";
        }
      }
    }
  }
  return sRetType;
}

export function checkEnableDirectBL(vType: string, costs: OpAcct[], sales: OpAcct[]) {
  const fCheckDutyTypeRow = (opAcctList: OpAcct[]): number => {
    for (const opAcct of opAcctList.filter(x => x.void === 0)) {
      if ((vType === "I" && [8, 18, 10, 20].includes(opAcct.chgType) || (vType === "E" && [10, 40].includes(opAcct.chgType))))
        return 1;
    }
    return 0;
  };

  const nResult = fCheckDutyTypeRow(costs) + fCheckDutyTypeRow(sales);
  return nResult === 0;
}

export function checkEnabledShipmentType(costs: OpAcct[], sales: OpAcct[], crdbNotes: SalesCostCrDb[]) {
  const sUsedShipmentType = getUsedShipmentTypeInGrid(costs, sales);
  let result = true;

  if (!_.isEmpty(sUsedShipmentType)) {
    result = false;
  }
  else {
    const invIssued: boolean = sales.some(w => Number(w.posted) > 1 && w.void === 0)
      || costs.some(w => Number(w.posted) > 1 && w.void === 0)
      || crdbNotes.some(w => w.void === 0);
    result = !invIssued;
  }
  return result;
}

export function checkLockAgent(mblAgent1Id: number, tbName: string, directBl: number, shipmentType: string, costs: OpAcct[], sales: OpAcct[], crdbNotes: SalesCostCrDb[]) {
  let sAgt1: number;
  let sAgt2: number = 0;
  let sAgt3: number = 0;
  //let sAgtName1: string;
  let sAgtName2: string = "";
  let sAgtName3: string = "";
  let mainAgentUsed: boolean = false;
  let result = true;
  sAgt1 = mblAgent1Id;

  const updateAgent = (agtId: number, agtName: string, agtAmt: number): void => {
    if (sAgt1 === agtId && agtAmt !== 0) mainAgentUsed = true;

    if (sAgt1 !== agtId && sAgt2 !== agtId && sAgt3 !== agtId) {
      if (sAgt2 === 0) {
        sAgt2 = agtId;
        sAgtName2 = agtName;
      }
      else if (sAgt3 === 0) {
        sAgt3 = agtId;
        sAgtName3 = agtName;
      }
    }
  };

  for (const opAcct of costs) {
    if (opAcct.agent > 0) updateAgent(opAcct.agent, opAcct.agentName, opAcct.agentAmt);
    if ([5, 6, 7, 8, 15, 16, 17, 40].includes(opAcct.chgType)) updateAgent(opAcct.entity, opAcct.entityName, -1);
  }

  for (const opAcct of sales) {
    if (opAcct.agent > 0) updateAgent(opAcct.agent, opAcct.agentName, opAcct.agentAmt);
    if ([5, 6, 7, 8, 15, 16, 17, 40].includes(opAcct.chgType)) updateAgent(opAcct.entity, opAcct.entityName, -1);
  }


  for (const crdbList of crdbNotes) {
    if (crdbList.agent > 0 && crdbList.void === 0) {
      if (crdbList.mhType === "M") updateAgent(crdbList.agent, crdbList.agentName, Number(crdbList.totalAmt));
    }
  }

  switch (tbName) {
    case "T_OIHMAIN":
      result = true;
      break;
    case "T_AIMMAIN":
    case "T_AOMMAIN":
    case "T_OIMMAIN":
    case "T_OOMMAIN":
      result = mainAgentUsed;
      break;
    default:
      result = mainAgentUsed || (directBl === 1 && shipmentType === 'D');
      break;
  }

  return result;
}

export const toTitleCase = (str: string): string => {
  return str?.replace(/(^|\s)\S/g, (t) => t.toUpperCase()) || '';
};

export function normalizeText(value?: string | null) {
  return (value ?? '').replace(/[\t\r\n]/g, '').trim();
};

// SingleLine
export function normalizeSingleText(value?: string | null) {
  return (value ?? '').replace(/[\t\r\n]/g, '')
                      .replace(/[\u000B\u000C\u0000-\u0008\u000E-\u001F\u007F\u0080-\u009F]/g, '') // 제어문자
                      .replace(/[\u00A0\u1680\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF\uFFFD]/g, '') // 유니코드 특수 공백, 보이지 않는 문자, UTF-8 깨짐 문자
                      .trim();
};

// MultiLine
export function normalizeMultiText(value?: string | null) {
  return (value ?? '').replace(/^[ ]+/g, '').replace(/[ ]+$/g, '')
                      .replace(/[\u000B\u000C\u0000-\u0008\u000E-\u001F\u007F\u0080-\u009F]/g, '')
                      .replace(/[\u00A0\u1680\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF\uFFFD]/g, '');
};

export function removeTab(value?: string | null) {
  return (value ?? '').replace(/\t/g, '');
}

//type이 string인 경우 텍스트에 Tab 문자가 포함된 경우 Tab문자를 제거하도록 처리
export function removeTabFromObject<T extends Record<string, any>>(change: T): T {
  const result = { ...change };
  for (const key in result) {
    if (typeof result[key] === 'string') {
      result[key] = removeTab(result[key]) as T[typeof key];
    }
  }
  return result;
}

export function getListUrlByJobName(jobName : string){
    switch(jobName){
      case JobNames.OIMbl:                    return `/common/joblistview/mbl/oceanImport`;
      case JobNames.OIHbl:                    return `/common/joblistview/hbl/oceanImport`;
      case JobNames.OEMbl:                    return `/common/joblistview/oeMbl/oceanExport`;
      case JobNames.OEHbl:                    return `/common/joblistview/oeHbl/oceanExport`;

      case JobNames.AIMbl:                    return `/common/joblistview/aiMawb/airImport`;
      case JobNames.AIHbl:                    return `/common/joblistview/aiHawb/airImport`;
      case JobNames.AEMbl:                    return `/common/joblistview/aeMawb/airExport`;
      case JobNames.AEHbl:                    return `/common/joblistview/aeHawb/airExport`;
      case JobNames.OtherOP:                  return `/common/joblistview/otherShipment/otherShipment`;
      case JobNames.WHRcv:                    return `/common/joblistview/whReceiving/warehousing`;
      case JobNames.WHShip:                   return `/common/joblistview/whShipping/warehousing`;
      case JobNames.WHRcpt:                   return `/common/joblistview/whReceipt/whReceipt`;
      
      case JobNames.Invoice:                  return `/common/joblistview/invoice/accounting?parentOp=billings`;
      case JobNames.WHStorageInv:             return `/common/joblistview/whStorageInvoice/warehousing`;
      case JobNames.GInvo:                    return `/common/joblistview/acGeneralInvoice/accounting?parentOp=billings`;
      case JobNames.CrDb:                     return `/common/joblistview/crdb/accounting?parentOp=billings`;
      case JobNames.Bill:                     return `/common/joblistview/bill/accounting?parentOp=billings`;
      case JobNames.GBill:                    return `/common/joblistview/acGeneralBill/accounting?parentOp=billings`;
      case JobNames.CustPayment:              return `/common/joblistview/acCustomerPayment/accounting`;
      case JobNames.BillPayment:              return `/common/joblistview/acBillPayment/accounting`;
      case JobNames.AgntPayment:              return `/common/joblistview/acAgentPayment/accounting`;
      case JobNames.CCPayment:                return `/common/joblistview/acCCPayment/accounting`;
      
      case JobNames.Company:                  return `/common/joblistview/company/customer`;
      case JobNames.OtQuotation:              return `/common/joblistview/otQuotation/otherShipment?parentOp=otherShipment`;
      case JobNames.OIQuotation:              return `/common/joblistview/oiQuotation/oceanImport?parentOp=oceanImport`;
      case JobNames.OEQuotation:              return `/common/joblistview/oeQuotation/oceanExport?parentOp=oceanExport`;
      case JobNames.AIQuotation:              return `/common/joblistview/aiQuotation/airImport?parentOp=airImport`;
      case JobNames.AEQuotation:              return `/common/joblistview/aeQuotation/airExport?parentOp=airExport`;
      case JobNames.OILoad:                   return `/common/joblistview/OILD/oceanImport`;
     
      case JobNames.OIContainer:              return `/common/joblistview/OICN/oceanImport`;
      case JobNames.OEContainer:              return `/common/joblistview/OECN/oceanExport`;
      case JobNames.OtherContainer:           return `/common/joblistview/OTOPC/otherShipment`;

      default : return '';
    }
  }

  
export function getOredrByJobName(jobName: string) {
  switch (jobName) {
    case JobNames.OIMbl:
    case JobNames.OIHbl:
    case JobNames.OEMbl:
    case JobNames.OEHbl:
    case JobNames.AIMbl:
    case JobNames.AIHbl:
    case JobNames.AEMbl:
    case JobNames.AEHbl:
    case JobNames.OtherOP:
    case JobNames.WHRcv:
    case JobNames.WHShip:
    case JobNames.WHRcpt:
    case JobNames.Invoice:
    case JobNames.WHStorageInv:
    case JobNames.GInvo:
    case JobNames.CrDb:
    case JobNames.CustPayment:
    case JobNames.BillPayment:
    case JobNames.AgntPayment:
    case JobNames.CCPayment:
    case JobNames.Company:
    case JobNames.OtQuotation:
    case JobNames.OIQuotation:
    case JobNames.OEQuotation:
    case JobNames.AIQuotation:
    case JobNames.AEQuotation:
      return 'u2Date';
    case JobNames.Bill:
    case JobNames.GBill:
      return 'updatedDate';
    case JobNames.OILoad:
      return 'u2Date';
    case JobNames.OIContainer:
    case JobNames.OEContainer:
    case JobNames.OtherContainer:
      return '';
    default: return '';
  }
  }

export function getJobNameBySourceType(type: string){
  switch(type){
      case 'OIM':           return JobNames.OIMbl;           
      case 'OIH':           return JobNames.OIHbl;           
      case 'OEM':           return JobNames.OEMbl;           
      case 'OEH':           return JobNames.OEHbl;           
      case 'AIM':           return JobNames.AIMbl;           
      case 'AIH':           return JobNames.AIHbl;           
      case 'AEM':           return JobNames.AEMbl;           
      case 'AEH':           return JobNames.AEHbl;           
      case 'OTH':           return JobNames.OtherOP;         
      case 'WHR':           return JobNames.WHRcv;           
      case 'WHS':           return JobNames.WHShip;          
      case 'C. Pay':        return JobNames.CustPayment;     
      case 'B. Pay':        return JobNames.BillPayment;     
      case 'CC Pay':        return JobNames.CCPayment;       
      case 'Agent. S':      return JobNames.AgntPayment;     
      case 'Payroll':       return JobNames.Payroll2;        
      case 'INV':           return JobNames.Invoice;         
      case 'BIL':           return JobNames.Bill;            
      case 'G.INV':         return JobNames.GInvo;           
      case 'G.BIL':         return JobNames.GBill;           
      case 'CrDb':          return JobNames.CrDb;            
      case 'Company':       return JobNames.Company;         

      case 'OICNTR':        return JobNames.OIContainer;     
      case 'OECNTR':        return JobNames.OEContainer;     
      case 'OTCNTR':        return JobNames.OtherContainer;  

      case 'OIQUOTE':       return JobNames.OIQuotation;     
      case 'OEQUOTE':       return JobNames.OEQuotation;     
      case 'AIQUOTE':       return JobNames.AIQuotation;     
      case 'AEQUOTE':       return JobNames.AEQuotation;     
      case 'OTQUOTE':       return JobNames.OtQuotation;     

      case 'TaxReturn':     return JobNames.SalesTaxReturn;  
      case 'BankTransfer':  return JobNames.BankTransfer;    
      case 'BankRecon':     return JobNames.BankRecon;       
      case 'F/R':           return JobNames.WHRcpt;
      case 'S.Inv':         return JobNames.WHStorageInv;

      default: return JobNames.None;
  }
}

export function getDetailUrlByJobName(jobName: string, jobId: number, bltbId: number = 0, bltbName: string = ''){

  switch(jobName){
      case JobNames.OIMbl:                    return `/oceanImport/mbl/view/${jobId}`;
      case JobNames.OIHbl:                    return `/oceanImport/hbl/view/${jobId}`;
      case JobNames.OEMbl:                    return `/oceanExport/mbl/view/${jobId}`;
      case JobNames.OEHbl:                    return `/oceanExport/hbl/view/${jobId}`;

      case JobNames.AIMbl:                    return `/airImport/mawb/view/${jobId}`;
      case JobNames.AIHbl:                    return `/airImport/hawb/view/${jobId}`;
      case JobNames.AEMbl:                    return `/airExport/mawb/view/${jobId}`;
      case JobNames.AEHbl:                    return `/airExport/hawb/view/${jobId}`;
      case JobNames.OtherOP:                  return `/otherShipment/otherShipment/view/${jobId}`;
      case JobNames.WHRcv:                    return `/warehousing/receiving/view/${jobId}`;
      case JobNames.WHShip:                   return `/warehousing/shipping/view/${jobId}`;
      case JobNames.WHRcpt:                   return `/warehousing/receipt/view/${jobId}`;
     
      case JobNames.Invoice:                  return `/accounting/invoice/view/${getJobNameByTbName(bltbName)}/${bltbId}/${jobId}`;
      case JobNames.GInvo:                    return `/accounting/acGeneralInvoice/view/${jobId}`;
      case JobNames.WHStorageInv:             return `/warehousing/storageInvoice/view/${jobId}`;
      case JobNames.CrDb:                     return `/accounting/crdb/view/${getJobNameByTbName(bltbName)}/${bltbId}/${jobId}`;
      case JobNames.Bill:                     return `/accounting/bill/view/${getJobNameByTbName(bltbName)}/${bltbId}/${jobId}`;
      case JobNames.GBill:                    return `/accounting/acGeneralBill/view/${jobId}`;
      case JobNames.CustPayment:              return `/accounting/acCustomerPayment/view/${jobId}`;
      case JobNames.BillPayment:              return `/accounting/acBillPayment/view/${jobId}`;
      case JobNames.AgntPayment:              return `/accounting/acAgentPayment/view/${jobId}`;
      case JobNames.CCPayment:                return `/accounting/acCCPayment/view/${jobId}`;
      case JobNames.Payroll2:                 return `/accounting/acPayroll2/view/${jobId}`;
      case JobNames.OIQuotation:              return `/oceanImport/quotation/view/${jobId}`;
      case JobNames.OEQuotation:              return `/oceanExport/quotation/view/${jobId}`;
      case JobNames.AIQuotation:              return `/airImport/quotation/view/${jobId}`;
      case JobNames.AEQuotation:              return `/airExport/quotation/view/${jobId}`;
      case JobNames.OtQuotation:              return `/otherShipment/quotation/view/${jobId}`;
      case JobNames.BankRecon:                return `/accounting/acBankRecon/view/${jobId}`;
      case JobNames.BankTransfer:             return `/accounting/acBankTransfer/view/${jobId}`;
      case JobNames.SalesTaxReturn:           return `/accounting/acSalesTaxReturn/view/${jobId}`;
      case JobNames.Company:                  return `/common/companies/view/${jobId}`;

      default : return '';
    }
  }


export function getListUrlWithSecCheck(appContext: any, jobName: JobNames, showGlobalOkDialog: any){

  let jobTaskSec = getSecTaskByJobName(jobName);
  const secTask = findSecurityTask(appContext, jobTaskSec as FmsSecTask);
  if(!secTask.grantRead){
    showGlobalOkDialog('Please check your user security!!!');
    return '';
  } 

  return getListUrlByJobName(jobName);  
}

export function getDetailSecLinkInfo(jobName: JobNames, jobId: number, bltbId: number = 0, bltbName: string = ''){
    return {
      secTask: getSecTaskByJobName(jobName) as FmsSecTask,
      detailUrl: getDetailUrlByJobName(jobName, jobId, bltbId, bltbName),
  };
}

export function validateEmails(emails: string): boolean {
  const emailArray: string[] = emails.split(';');
  try {
    for (const email of emailArray) {
      const isValid: boolean = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
      if (!isValid) return false;
    }
    return true;
  } catch (error) {
    return false;
  }
}